import {assert} from 'Asserts';

import {processTimingEvent} from './BenchlingProfiler';
import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

const DOMAIN = ProfilerEventDomain.LAB_AUTOMATION;

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_LAB_AUTOMATION_EVENTS_VERSION = 1;

export enum ProfilerLabAutomationFeatures {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE = 'labAutomationRetrieveConnectFile',
  LAB_AUTOMATION_CREATE_CONNECT_FILE = 'labAutomationCreateConnectFIle',
  LAB_AUTOMATION_PROCESS_OUTPUT_FILE = 'labAutomationProcessOutputFile',
}
enum ProfilerLabAutomationActions {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED = 'labAutomationRetrieveConnectFileStarted',
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED = 'labAutomationRetrieveConnectFileEnded',
  LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED = 'labAutomationCreateConnectFIleStarted',
  LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED = 'labAutomationCreateConnectFIleEnded',
  LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED = 'labAutomationProcessOutputFileStarted',
  LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED = 'labAutomationProcessOutputFileEnded',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerLabAutomationFeatures]: Function} = {
  [ProfilerLabAutomationFeatures.LAB_AUTOMATION_RETRIEVE_CONNECT_FILE]: processRetrieveConnectFile,
  [ProfilerLabAutomationFeatures.LAB_AUTOMATION_CREATE_CONNECT_FILE]: processCreateConnectFIle,
  [ProfilerLabAutomationFeatures.LAB_AUTOMATION_PROCESS_OUTPUT_FILE]: processProcessOutputFile,
};

export const LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_RETRIEVE_CONNECT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED,
};
export const LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_RETRIEVE_CONNECT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED,
};
export const LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_CREATE_CONNECT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED,
};
export const LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_CREATE_CONNECT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED,
};
export const LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_PROCESS_OUTPUT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED,
};
export const LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerLabAutomationFeatures.LAB_AUTOMATION_PROCESS_OUTPUT_FILE,
  action: ProfilerLabAutomationActions.LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED,
};

export function processLabAutomationEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerLabAutomationFeatures;
  assert(
    FEATURE_TO_PROCESSING_FUNCTION.hasOwnProperty(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature](profilerEvent);
}

function processRetrieveConnectFile(): void {
  processTimingEvent(
    LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT,
    LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT,
    'Lab Automation Retrieve Connect Files',
    'perf.lab_automation_retrieve_connect_files',
    PROFILER_LAB_AUTOMATION_EVENTS_VERSION
  );
}

function processCreateConnectFIle(): void {
  processTimingEvent(
    LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT,
    LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT,
    'Lab Automation Create Connect Files',
    'perf.lab_automation_create_connect_files',
    PROFILER_LAB_AUTOMATION_EVENTS_VERSION
  );
}

function processProcessOutputFile(): void {
  processTimingEvent(
    LAB_AUTOMATION_PROCESS_OUTPUT_FILE_STARTED_EVENT,
    LAB_AUTOMATION_PROCESS_OUTPUT_FILE_ENDED_EVENT,
    'Lab Automation Process Output File',
    'perf.lab_automation_process_output_file',
    PROFILER_LAB_AUTOMATION_EVENTS_VERSION
  );
}
